const defaultTheme = require("tailwindcss/defaultTheme");
const colors = require("tailwindcss/colors");

// Height of the fixed top rail (ProjectTopRailV2). `h-top-rail` and the
// `*-below-top-rail` utilities derive from this single value: 40px, or 48px under the
// category nav, whose layout root sets --top-rail-height (LayoutV2).
const TOP_RAIL_HEIGHT = "var(--top-rail-height, 40px)";
// The content box of LayoutV2's pane: the viewport below the top rail and above the assistant
// rail, less the pane's 1px top and bottom strokes.
const BELOW_TOP_RAIL_HEIGHT = `calc(100vh - ${TOP_RAIL_HEIGHT} - var(--assistant-bottom-rail-height, 0px) - 2px)`;

module.exports = {
  mode: "jit",
  // Dark mode applies to `.dark` and its descendants, EXCEPT subtrees marked
  // `.theme-light`. Lets the design-system test pages render both a light and
  // a dark showcase side-by-side regardless of the page's global theme.
  // `:where(...)` is used to keep the variant specificity at (0,1,0) so it
  // doesn't unintentionally override unrelated global styles.
  darkMode: ["variant", "&:where(.dark, .dark *):not(:where(.theme-light, .theme-light *))"],
  content: [
    "./pages/**/*.{js,ts,jsx,tsx}",
    "./components/**/*.{js,ts,jsx,tsx}",
    "./constants/**/*.{js,ts,jsx,tsx}",
    "./utils/**/*.{js,ts,jsx,tsx}",
  ],
  theme: {
    extend: {
      gridTemplateColumns: {
        13: "repeat(13, minmax(0, 1fr))",
        14: "repeat(14, minmax(0, 1fr))",
        15: "repeat(15, minmax(0, 1fr))",
        16: "repeat(16, minmax(0, 1fr))",
        17: "repeat(17, minmax(0, 1fr))",
        18: "repeat(18, minmax(0, 1fr))",
        19: "repeat(19, minmax(0, 1fr))",
        20: "repeat(20, minmax(0, 1fr))",
        21: "repeat(21, minmax(0, 1fr))",
        22: "repeat(22, minmax(0, 1fr))",
        23: "repeat(23, minmax(0, 1fr))",
        24: "repeat(24, minmax(0, 1fr))",
      },
      gridColumnStart: {
        13: "13",
        14: "14",
        15: "15",
        16: "16",
        17: "17",
        18: "18",
        19: "19",
        20: "20",
        21: "21",
        22: "22",
        23: "23",
        24: "24",
      },
      gridColumn: {
        "span-13": "span 13 / span 13",
        "span-14": "span 14 / span 14",
        "span-15": "span 15 / span 15",
        "span-16": "span 16 / span 16",
        "span-17": "span 17 / span 17",
        "span-18": "span 18 / span 18",
        "span-19": "span 19 / span 19",
        "span-20": "span 20 / span 20",
        "span-21": "span 21 / span 21",
        "span-22": "span 22 / span 22",
        "span-23": "span 23 / span 23",
        "span-24": "span 24 / span 24",
      },
      fontFamily: {
        sans: ["Everett", ...defaultTheme.fontFamily.sans],
        // `font-mono` is the in-product (logged-in dashboard) mono face. It deliberately does NOT
        // use Everett Mono: that face is a marketing display font and is hard to read in dense
        // product UI. Marketing surfaces must ask for Everett Mono explicitly via `font-marketing-mono`.
        // Kept byte-identical to `--dashboard-font-mono` in styles/design-system-typography.css,
        // which declares the matching @font-face; update both together.
        mono: [
          "Chalk Dashboard JetBrains Mono",
          "JetBrains Mono Variable",
          "JetBrains Mono",
          ...defaultTheme.fontFamily.mono,
        ],
        acumin: ["Acumin Variable Concept", "ui-sans-serif", "system-ui", "sans-serif"],
        everett: ["Everett", "sans-serif"],
        // Everett Mono is the marketing display mono. Named for where it belongs rather than for the
        // typeface so that dashboard code has no reason to reach for it; see `mono` above. Falls back
        // to the generic mono stack, not sans: Everett Mono is a webfont, and the marketing surfaces
        // that use it (terminals, code samples, the animated editors) depend on fixed advance widths
        // during the font-swap window.
        "marketing-mono": ["Everett Mono", ...defaultTheme.fontFamily.mono],
        everettlight: ["EverettLight", "sans-serif"],
        digital7: ["Digital-7", "monospace"],
      },
      fontWeight: {
        "cds-tab": "450",
      },
      colors: {
        "primary-green": {
          50: "#f0f7f6", // unofficial
          100: "#70ce97", // unofficial
          200: "#274a43", // "1"
          300: "#1d3b34", // "2"
          350: "#29524a", // "2.5?"
          400: "#274a43", // "3"
          500: "#29524a", // "4"
          600: "#1d3b34", // "5"
          700: "#06261f", // "6"
          800: "#04160e", // unofficial
          900: "#020b07", // unofficial
        },
        "secondary-lime": {
          50: "#F7FBDD", // unofficial
          100: "#EFF6C3", // unofficial
          200: "#E4F1AA", // unofficial
          300: "#D8EB91", // unofficial
          400: "#CAE579", // unofficial
          500: "#a8db4a", // pdf lg 1
          600: "#94BD52", // unofficial
          700: "#729D42", // unofficial
          800: "#527C33", // unofficial
          900: "#375B25", // unofficial
        },
        marketing: {
          darkgreen: {
            logo: "#ffffff",
            bg: "#224a41",
            // bg: "#1e3532",
            title: "#a8db4a",
            subtitle: "#d4d4d8",
            header: "#ffffff",
            navText: "#B8CEC6",
            navHighlight: "#ffffff",
            textPrimary: "#ffffff",
            textSecondary: "#ffffff",
          },
          mediumgreen: {
            logo: "#ffffff",
            bg: "#32645B",
            title: "#B3DA5B",
            subtitle: "#D0E1DB",
            header: "#ffffff",
            navText: "#B8CEC6",
            navHighlight: "#274a43",
            textPrimary: "#D0E1DB",
            textSecondary: "#D0E1DB",
          },
          "seafoam-cms": {
            logo: "#1D3D36",
            bg: "#73d19c",
            title: "#1D3D36",
            subtitle: "#1D3D36",
            header: "#1D3D36",
            navText: "#1D3D36",
            navHighlight: "#224a41",
            textPrimary: "#1D3D36",
            textSecondary: "#1D3D36",
          },
          "mintgreen-cms": {
            logo: "#224a41",
            bg: "#70CD96",
            title: "#224a41",
            subtitle: "#224a41",
            header: "#224a41",
            navText: "#224a41",
            navHighlight: "#ffffff",
            textPrimary: "#224a41",
            textSecondary: "#224a41",
          },
          "violet-light-cms": {
            logo: "#6B3A8E",
            bg: "#FFFFFF",
            title: "#6B3A8E",
            subtitle: "#6B3A8E",
            header: "#6B3A8E",
            navText: "#6B3A8E",
            navHighlight: "#6B3A8E",
            textPrimary: "#6B3A8E",
            textSecondary: "#6B3A8E",
          },
          "persian-fable-green-cms": {
            logo: "#224a41",
            bg: "#D4EBDD",
            title: "#224a41",
            subtitle: "#224a41",
            header: "#224a41",
            navText: "#224a41",
            navHighlight: "#ffffff",
            textPrimary: "#224a41",
            textSecondary: "#224a41",
          },
          "navyblue-cms": {
            logo: "#70CD96",
            bg: "#253159",
            title: "#70CD96",
            subtitle: "#70CD96",
            header: "#70CD96",
            navText: "#70CD96",
            navHighlight: "#70CD96",
            textPrimary: "#70CD96",
            textSecondary: "#70CD96",
          },
          "purple-cms": {
            logo: "#FFFFFF",
            bg: "#5C2D83",
            title: "#FFFFFF",
            subtitle: "#FFFFFF",
            header: "#FFFFFF",
            navText: "#FFFFFF",
            navHighlight: "#FFFFFF",
            textPrimary: "#FFFFFF",
            textSecondary: "#FFFFFF",
          },
          lightgreen: {
            logo: "#264a43",
            bg: "#b9cfc7",
            title: "#1c3b34",
            subtitle: "#3F7067",
            header: "#1c3b34",
            navText: "#264A43",
            navHighlight: "#ffffff",
            textPrimary: "#1c3b34",
            textSecondary: "#3F7067",
          },
          lightgray: {
            logo: "#264a43",
            bg: "#efefef",
            title: "#1c3b34",
            subtitle: "#000000",
            header: "#1c3b34",
            navText: "#33514A",
            navHighlight: "#70ce97",
            textPrimary: "#224A41",
            textSecondary: "#20433b",
          },
          slate: {
            logo: "#ffffff",
            bg: "#45494c",
            title: "#81ceea",
            subtitle: "#B8CEC6",
            header: "#81ceea",
            navText: "#B8CEC6",
            navHighlight: "#274a43",
            textSecondary: "#b8cec6",
            textPrimary: "#ffffff",
          },
          colors: {
            lightgreen: "#70ce97",
            yellow: "#f4f762",
            orange: "#f9957f",
            purple: "#b39bfa",
            blue: "#81ceea",
          },
          green: {
            1: "#224a41",
            2: "#1D3D36",
            3: "#AEC6BE",
            4: "#2E5A49",
            5: "#BBD96D",
            6: "#B9CFC7",
            7: "#233A34",
            8: "#224A41",
            9: "#D0E1DB",
            10: "#EFF5F3",
            11: "#CDD5D2",
            12: "#CDE3DB",
            13: "#AEC6BD",
            14: "#4C6961",
            15: "#97ADA5",
            16: "#648680",
            17: "#7B958D",
            18: "#2A574E",
            19: "#EEFEF7",
            20: "#E4F8EF",
            21: "#218E61",
            50: "#B8CEC6",
            100: "#7BA392",
            200: "#3F7067",
            300: "#32645B",
            400: "#29524A",
            500: "#264A43",
            600: "#1C3B34",
            700: "#0C3129",
            800: "#06261F",
            900: "#041D17",
          },
          gray: {
            1: "#F7F7F7",
            2: "#EFEFEF",
            3: "#89909E",
            4: "#E5E5E5",
          },
          text: {
            primary: {
              light: "#EFF5F3",
              "less-dark": "#224A41",
              dark: "#173029",
              banner: "#18181B",
            },
          },
          "mint-green": "#70CD96",
          "mint-green-dark": "#279467",
          "persian-fable-green": "#D4EBDD",
          // chartreuse: "#B3DA5B",
          chartreuse: "#BBD96D",
          "chartreuse-dark": "#9BBD00",
          "columbia-blue": "#81CEEA",
          fuchsia: "#F17592",
          lavender: "#B29AF9",
          magenta: "#F57596",
          "navy-blue": "#253159",
          orange: "#F8947F",
          purple: "#9E73D5",
          violet: "#5C2D83",
          salmon: "#FC9982",
          sienna: "#EC6E47",
          "sky-blue": "#C2FAFC",
          yellow: "#F3F662",
        },
        "marketing-nav-bar-bottom": "#2c5850",
        "some-light-green": "#31645b",
        "marketing-bottom-light": "#b9cfc6",
        "some-dark-green": "#103c34",
        "text-orange": "#f9957f",
        "text-purple": "#b39bfa",
        "accent-green": "#2a524a",
        "accent-red": "#f47697",
        "accent-purple": "#b6a2fc",
        "accent-seafoam": "#73d19c",
        "accent-yellow": "#f9f96b",
        "accent-lime": "#badd62",
        "accent-orange": "#fc9881",
        "mint-green": "#70CD96",
        "mint-green-dark": "#279467",
        // cds == "chalk design system"
        cds: {
          text: {
            primary: "#173029",
            secondary: "#7a8a87",
          },
          "mint-green": "#70CD96",
          "mint-green-dark": "#279467",
          chartreuse: "#B3DA5B",
          yellow: "#F3F662",
          orange: "#F8947F",
          fuchsia: "#F17592",
          lavender: "#B29AF9",
          purple: "#B5A3FC",
          "columbia-blue": "#81CEEA",
          "light-orange": "#DBB111",
          grapefruit: "#EE9D86",
          icons: {
            warning: "#DBB111",
            danger: "#C95736",
          },
          logos: {
            default: "#B7CDC5",
            melio: "#6D40FF",
          },
          // color palettes generated by https://materialpalettes.com/
          // colors in the design system are labeled
          "dark-green": {
            50: "#e4f3f2",
            100: "#bbe1de",
            200: "#91cec9",
            300: "#6cbbb3",
            400: "#56aba3",
            500: "#4c9c92",
            600: "#468f85",
            700: "#417e75",
            800: "#3c6e65",
            900: "#33514a", // icons-primary, dark-background
          },
          "light-gray": {
            50: "#f9fcfb",
            100: "#f5f8f7", // light-background
            200: "#f0f3f2",
            250: "#efefef", // divider
            300: "#e3e6e5",
            400: "#c1c4c3",
            500: "#a2a5a4",
            600: "#797c7b",
            700: "#656766",
            800: "#464847",
            900: "#242626",
          },
          gray: {
            25: "#f7f7f7",
            50: "#eeeeee",
            100: "#d4d4d4",
            200: "#b4b9b8",
            250: "#cfcfcf",
            300: "#939e9c",
            400: "#7a8a87", // icons-secondary, type-secondary
            450: "#666666", // resolver details title
            500: "#627773",
            600: "#566864",
            700: "#475451",
            800: "#39413f",
            900: "#282c2b",
          },
          "black-green": {
            50: "#dff2ed",
            100: "#c3d9d0",
            200: "#a2beb4",
            300: "#81a398",
            400: "#688f83",
            500: "#507b6e",
            600: "#456c5f",
            700: "#36584d",
            725: "#2f4943", // undocumented mid-dark text color
            750: "#2f3f3a", // undocumented dark text color
            800: "#28453d",
            900: "#173029", // type-primary
          },
          "light-green": {
            50: "#e9f6ed",
            100: "#cae8d2",
            200: "#a9dab7",
            300: "#87cc9b", // icons-positive
            400: "#6ec185",
            500: "#52b66f",
            600: "#4aa765",
            700: "#409458",
            800: "#37834d",
            900: "#276439",
          },
          "bright-green": {
            50: "#e5f4e9",
            100: "#c1e4c9",
            200: "#9ad2a7",
            300: "#70c285",
            400: "#4fb56c", // type-link
            500: "#2aa853",
            600: "#219949",
            700: "#16883e",
            800: "#097733",
            900: "#005820",
          },
          "brick-red": {
            50: "#f7eae9",
            100: "#f8cfc4",
            200: "#f5b19e",
            300: "#f39378",
            400: "#f17d5b",
            500: "#f16942",
            600: "#e6633e",
            700: "#d85c39",
            800: "#c95636", // icons-negative
            900: "#ad4c30",
          },
          pastel: {
            orange: "#ee9d86",
            purple: "#b787f5",
            "purple-dark": "#A77BDF",
            green: "#bcda6e",
            blue: "#95c7cb",
          },

          // Chalk Design System — component color tokens.
          // Organized as {component/role}-{part}-{state}[-dark].
          // Fills that are gradients live in theme.backgroundImage below;
          // strokes and other solid colors live here.
          // Primary uses a single shade set across light and dark modes —
          // the two were visually near-identical so the DS dropped the
          // -dark variants.
          primary: {
            "stroke-default": "#12654F",
            "stroke-hover": "#105C48",
          },

          // Text links share one treatment across ordinary and table surfaces.
          // The light default clears WCAG AA on surface-subtle (#fdfdfd), the
          // lightest dashboard surface where links commonly appear.
          link: {
            "text-default": "#14853c",
            "text-hover": "#005820",
            "text-default-dark": "#2aa853",
            "text-hover-dark": "#4fb56c",
          },

          // Neutral surface tokens — light-mode secondary/tertiary button
          // fills and the shared disabled treatment. Named distinctly from
          // Tailwind's `white` / `zinc-*` palette so `styles/dark-mode.css`
          // global `.dark .X` overrides don't re-map them when a component
          // needs to render its own theme regardless of the global theme.
          surface: {
            default: "#ffffff", // bg-cds-surface-default  (= white)
            subtle: "#fdfdfd", //  bg-cds-surface-subtle   (= large light surfaces)
            hover: "#fafafa", //   bg-cds-surface-hover    (= zinc-50 / chart cards)
            active: "#f4f4f5", //  bg-cds-surface-active   (= zinc-100)
            border: "#e4e4e7", //  border-cds-surface-border (= zinc-200)
          },

          // Dark-mode-only elevated surface for popovers, menu containers,
          // and other floating chrome. Harmonizes with the project's
          // `--bg-base` (#09090B) and `--bg-inset` (#0F0F12) in even +6
          // RGB steps, so base → inset → elevated → raised reads as one ladder.
          "base-dark": "#09090B", // bg-cds-base-dark
          "inset-dark": "#0F0F12", // bg-cds-inset-dark
          "elevated-dark": "#15151A", // bg-cds-elevated-dark
          "raised-dark": "#1B1B22", // bg-cds-raised-dark

          // Body + muted text. Distinct class names from `cds.text.primary`
          // / `cds.text.secondary` (which ARE intentionally remapped by
          // dark-mode.css) so DS components can hold a stable color.
          // Values match Tailwind zinc-800 / zinc-400 / zinc-500 per the
          // Figma spec; we wrap them in DS-owned class names so
          // dark-mode.css's `.dark .text-zinc-*` overrides don't flip
          // them out from under us.
          ink: "#27272a", //             text-cds-ink            (zinc-800)
          "ink-muted": "#a1a1aa", //       text-cds-ink-muted      (zinc-400)
          "ink-subtle": "#71717a", //      text-cds-ink-subtle     (zinc-500)
          "ink-secondary": "#52525b", //   text-cds-ink-secondary  (zinc-600)
          "ink-medium": "#3f3f46", //      text-cds-ink-medium     (zinc-700)
          "ink-strong": "#18181b", //      text-cds-ink-strong     (zinc-900)
          // `ink-subtle` is used for dark-mode placeholder text —
          // one step more muted than ink-muted.

          // Error / destructive accent. Shared between input error borders,
          // inline error messages, and required-field asterisks.
          error: "#e01c40", //      text-cds-error, border-cds-error

          // Status badge tones. These intentionally use Tailwind palette
          // stops as their source of truth so status colors remain familiar
          // and easy to audit while still exposing DS-owned class names.
          status: {
            green: {
              fill: colors.emerald[100],
              border: colors.emerald[300],
              text: colors.emerald[800],
              "fill-dark": colors.emerald[950],
              "border-dark": colors.emerald[800],
              "text-dark": colors.emerald[300],
            },
            yellow: {
              fill: colors.amber[100],
              border: colors.amber[300],
              text: colors.amber[800],
              "fill-dark": colors.amber[950],
              "border-dark": colors.amber[800],
              "text-dark": colors.amber[300],
            },
            orange: {
              fill: colors.orange[100],
              border: colors.orange[300],
              text: colors.orange[800],
              "fill-dark": colors.orange[950],
              "border-dark": colors.orange[800],
              "text-dark": colors.orange[300],
            },
            red: {
              fill: colors.rose[100],
              border: colors.rose[300],
              text: colors.rose[800],
              "fill-dark": colors.rose[950],
              "border-dark": colors.rose[800],
              "text-dark": colors.rose[300],
            },
            neutral: {
              fill: colors.slate[100],
              border: colors.slate[300],
              text: colors.slate[700],
              "fill-dark": colors.zinc[800],
              "border-dark": colors.zinc[700],
              "text-dark": colors.zinc[300],
            },
            blue: {
              fill: colors.sky[100],
              border: colors.sky[300],
              text: colors.sky[800],
              "fill-dark": colors.sky[950],
              "border-dark": colors.sky[800],
              "text-dark": colors.sky[300],
            },
            purple: {
              fill: colors.purple[100],
              border: colors.purple[300],
              text: colors.purple[800],
              "fill-dark": colors.purple[950],
              "border-dark": colors.purple[800],
              "text-dark": colors.purple[300],
            },
          },
        },
      },
      keyframes: {
        dot: {
          "0%, 100%": { transform: "scale(50%)" },
          "30%": { transform: "scale(140%)" },
          "80%": { transform: "scale(60%)" },
        },
        "spin-reverse": {
          from: {
            transform: "rotate(360deg)",
          },
          to: {
            transform: "rotate(0deg)",
          },
        },
        "placeholder-loading": {
          "0%, 100%": {
            opacity: 0.5,
            transform: "scale(100%)",
          },
          "50%": {
            opacity: 1,
            transform: "scale(105%)",
          },
        },
        "appear-grow": {
          from: { transform: "scale(0%)" },
          to: { transform: "scale(100%)" },
        },
        "sidenav-fade-in": {
          from: { opacity: "0" },
          to: { opacity: "1" },
        },
        "sidenav-panel-swap-left": {
          "0%": {
            opacity: "0.88",
            transform: "translateX(4px)",
          },
          "100%": {
            opacity: "1",
            transform: "translateX(0)",
          },
        },
        "sidenav-panel-swap-right": {
          "0%": {
            opacity: "0.88",
            transform: "translateX(-4px)",
          },
          "100%": {
            opacity: "1",
            transform: "translateX(0)",
          },
        },
        // A packet of light riding one edge of a container: it fades in off
        // the left, crosses at a constant rate, fades out off the right, then
        // waits out the rest of the cycle parked offscreen. The idle tail is
        // baked into the keyframes rather than expressed as a delay so that a
        // single `animationDuration` controls both the crossing speed and the
        // gap between passes (a delay would only postpone the first pass).
        // Every keyframe declares `transform` so the crossing stays linear no
        // matter which easing a caller applies. See `EdgeSweep`.
        // Arrival highlight for a new row in a live feed; fades to the row's own background.
        "live-row-in": {
          from: { backgroundColor: "rgb(16 185 129 / 0.18)" },
          to: { backgroundColor: "transparent" },
        },
        "edge-sweep": {
          "0%": { opacity: "0", transform: "translateX(-70%)" },
          "8%": { opacity: "1", transform: "translateX(-33%)" },
          "44%": { opacity: "1", transform: "translateX(133%)" },
          "52%": { opacity: "0", transform: "translateX(170%)" },
          "100%": { opacity: "0", transform: "translateX(170%)" },
        },
      },
      animation: {
        "appear-grow-fast": "appear-grow 150ms ease-in-out",
        "appear-grow": "appear-grow 300ms ease-in-out",
        "appear-grow-slow": "appear-grow 500ms ease-in-out",
        "placeholder-loading":
          "placeholder-loading 2s cubic-bezier(0.4, 0, 0.6, 1) infinite backwards",
        "spin-reverse": "spin-reverse 1s linear infinite backwards",
        dot: "dot 1000ms ease-in-out infinite backwards",
        "dot-1": "dot 1000ms ease-out infinite backwards",
        "dot-2": "dot 1000ms ease-out 200ms infinite backwards",
        "dot-3": "dot 1000ms ease-out 400ms infinite backwards",
        "sidenav-fade-in": "sidenav-fade-in 150ms ease-out both",
        "sidenav-panel-swap-left":
          "sidenav-panel-swap-left 300ms cubic-bezier(0.25, 0.1, 0.25, 1) both",
        "sidenav-panel-swap-right":
          "sidenav-panel-swap-right 300ms cubic-bezier(0.25, 0.1, 0.25, 1) both",
        "live-row-in": "live-row-in 1600ms ease-out",
        // Duration here is only a default; `EdgeSweep` overrides it inline.
        "edge-sweep": "edge-sweep 4200ms linear infinite",
      },
      screens: {
        xs: "475px",
        ...defaultTheme.screens,
      },
      transitionProperty: {
        border: "border",
      },
      boxShadow: {
        "inner-md": "inset 0 2px 4px 2px rgb(0 0 0 / 0.25)",

        // Chalk Design System effects (light mode).
        // `cds-s/m/l` are the canonical elevation shadows.
        // `cds-inset` is the pressed/active inset shadow.
        // `cds-focus` is a composite: green-500 @ 50% glow (1px spread)
        //   stacked on top of the cds-s base shadow.
        "cds-s": "0 1px 2px 0 rgb(0 0 0 / 0.03)",
        "cds-m": "0 2px 8px 0 rgb(0 0 0 / 0.08)",
        "cds-l": "0 8px 24px 0 rgb(0 0 0 / 0.12)",
        // Dark-mode parallel to `cds-m`. Same offset + blur shape;
        // opacity is bumped 5× because drop shadows have to do
        // heavier lifting on dark surfaces to register as elevation.
        "cds-m-dark": "0 2px 8px 0 rgb(0 0 0 / 0.4)",
        // Dark-mode parallel to `cds-l`. Uses the larger light-mode
        // elevation footprint with a heavier but not 5× opacity, so
        // popovers lift without looking like black halos.
        "cds-l-dark": "0 8px 24px 0 rgb(0 0 0 / 0.48)",
        "cds-inset": "inset 0 2px 4px 0 rgb(0 0 0 / 0.04)",
        "cds-focus": "0 0 0 1px rgb(59 176 137 / 0.5), 0 1px 2px 0 rgb(0 0 0 / 0.03)",
      },
      dropShadow: {
        md: ["0 2px 4px rgba(0, 0, 0, 0.08)", "0 1px 1px rgba(0, 0, 0, 0.06)"],
      },
      fontSize: {
        xxs: [".625rem", { lineHeight: "0.875rem", letterSpacing: "-0.02rem" }],
        // CSS-var fallbacks mirror the values declared on
        // `.design-system-typography` in styles/design-system-typography.css.
        // Without them, `var(--design-system-*)` returns no value when
        // the element isn't inside a `.design-system-typography`
        // ancestor (or if the CSS file hasn't loaded yet), and
        // browsers fall back to the initial 16px — which is how the
        // menu title rows were rendering before.
        "cds-2xs": [
          "var(--design-system-text-2xs-size, 0.625rem)",
          {
            lineHeight: "var(--design-system-text-2xs-line-height, 0.75rem)",
            letterSpacing: "var(--design-system-text-2xs-letter-spacing, 0.02rem)",
          },
        ],
        "cds-xs": [
          "var(--design-system-text-xs-size, 0.6875rem)",
          {
            lineHeight: "var(--design-system-text-xs-line-height, 1rem)",
            letterSpacing: "var(--design-system-text-xs-letter-spacing, 0.02rem)",
          },
        ],
        "cds-sm": [
          "var(--design-system-text-sm-size, 0.75rem)",
          {
            lineHeight: "var(--design-system-text-sm-line-height, 1rem)",
            letterSpacing: "var(--design-system-text-sm-letter-spacing, 0.01rem)",
          },
        ],
        // `cds-sm` with the taller body line-height: the `.type-text-sm-body`
        // token, for multi-line prose such as markdown and descriptions.
        "cds-body-sm": [
          "var(--design-system-text-sm-size, 0.75rem)",
          {
            lineHeight: "var(--design-system-text-sm-body-line-height, 1.25rem)",
            letterSpacing: "var(--design-system-text-sm-letter-spacing, 0.01rem)",
          },
        ],
        "cds-tab": [
          "var(--design-system-text-tab-size, 0.75rem)",
          {
            lineHeight: "var(--design-system-text-tab-line-height, 1rem)",
            letterSpacing: "var(--design-system-text-tab-letter-spacing, 0.12px)",
          },
        ],
        "cds-md": [
          "var(--design-system-text-md-size, 0.8125rem)",
          {
            lineHeight: "var(--design-system-text-md-line-height, 1rem)",
            letterSpacing: "var(--design-system-text-md-letter-spacing, 0)",
          },
        ],
        "cds-lg": [
          "var(--design-system-text-lg-size, 0.9375rem)",
          {
            lineHeight: "var(--design-system-text-lg-line-height, 1.25rem)",
            letterSpacing: "var(--design-system-text-lg-letter-spacing, 0)",
          },
        ],
        "cds-xl": [
          "var(--design-system-text-xl-size, 1.125rem)",
          {
            lineHeight: "var(--design-system-text-xl-line-height, 1.5rem)",
            letterSpacing: "var(--design-system-text-xl-letter-spacing, -0.01rem)",
          },
        ],
        "cds-h1": [
          "var(--design-system-h2-size, 1.5rem)",
          {
            lineHeight: "var(--design-system-h2-line-height, 1.75rem)",
            letterSpacing: "var(--design-system-h2-letter-spacing, -0.02rem)",
          },
        ],
        "cds-h2": [
          "var(--design-system-h3-size, 1.25rem)",
          {
            lineHeight: "var(--design-system-h3-line-height, 1.5rem)",
            letterSpacing: "var(--design-system-h3-letter-spacing, -0.015rem)",
          },
        ],
        "cds-h3": [
          "var(--design-system-text-xl-size, 1.125rem)",
          {
            lineHeight: "var(--design-system-text-xl-line-height, 1.5rem)",
            letterSpacing: "var(--design-system-text-xl-letter-spacing, -0.01rem)",
          },
        ],
        "cds-h4": [
          "var(--design-system-text-lg-size, 0.9375rem)",
          {
            lineHeight: "var(--design-system-text-lg-line-height, 1.25rem)",
          },
        ],
        "cds-h5": [
          "var(--design-system-text-sm-size, 0.75rem)",
          {
            lineHeight: "var(--design-system-text-sm-line-height, 1rem)",
            letterSpacing: "var(--design-system-text-sm-letter-spacing, 0.01rem)",
          },
        ],
        "cds-h6": [
          "var(--design-system-text-sm-size, 0.75rem)",
          {
            lineHeight: "var(--design-system-text-sm-line-height, 1rem)",
            letterSpacing: "var(--design-system-text-sm-letter-spacing, 0.01rem)",
          },
        ],
      },

      spacing: {
        "page-ui": "var(--page-ui-inset)",
        "page-text": "var(--page-text-inset)",
        // Chalk Design System canonical spacing scale.
        // Only these values should be used for padding, margin, and gap in
        // DS-owned components. Names match the pixel value for self-documentation.
        "cds-2": "2px",
        "cds-3": "3px",
        "cds-4": "4px",
        "cds-6": "6px",
        "cds-8": "8px",
        "cds-10": "10px",
        "cds-12": "12px",
        "cds-16": "16px",
        "cds-20": "20px",
        "cds-24": "24px",
        "cds-40": "40px",
      },

      // Chalk Design System canonical radii.
      borderRadius: {
        "cds-2": "2px",
        "cds-3": "3px",
        "cds-4": "4px",
        "cds-6": "6px",
      },

      // Chalk Design System — component fill gradients.
      // Fills are declared as background-image gradients uniformly so hover
      // and active transitions animate between images (not image <-> color).
      // "active" states use identical stops when the spec is a solid color.
      // Primary uses the same gradient set in light and dark modes —
      // monotonic darkening: default → hover → active.
      backgroundImage: {
        "cds-primary-fill-default": "linear-gradient(180deg, #177F65 0%, #15775F 100%)",
        "cds-primary-fill-hover": "linear-gradient(180deg, #15775F 0%, #146E57 100%)",
        "cds-primary-fill-active": "linear-gradient(180deg, #12654F 0%, #12654F 100%)",
      },

      // Allow things like min-w-24
      minWidth: ({ theme }) => ({
        ...theme("spacing"),
      }),
      // Allow things like max-w-24
      maxWidth: ({ theme }) => ({
        ...theme("spacing"),
      }),

      // Allow things like min-h-24
      minHeight: ({ theme }) => ({
        ...theme("spacing"),
        "below-top-rail": BELOW_TOP_RAIL_HEIGHT,
      }),
      height: {
        "top-rail": TOP_RAIL_HEIGHT,
        "below-top-rail": BELOW_TOP_RAIL_HEIGHT,
      },
      // Allow things like max-h-24
      maxHeight: ({ theme }) => ({
        ...theme("spacing"),
      }),

      // Chalk Design System — component widths.
      width: {
        "cds-dropdown": "220px",
      },
    },
  },
  plugins: [
    require("@tailwindcss/forms"),
    require("@tailwindcss/typography"),
    require("tailwindcss-opentype"),
    function ({ addVariant }) {
      addVariant("scrollbar", "&::-webkit-scrollbar");
      addVariant("h-lg", "@media (min-height: 600px)");
    },
  ],
};
